<script lang="ts">
import nuxtUiTextareaTheme from '#build/ui/textarea';
import { InlineDocument, InlineSuggestion, useFormField, useMergedTheme } from '#layers/ui';

export type { UserInvitationAttributes } from '#layers/access/utils/extensions/user-invitation';
</script>

<script setup lang="ts">
import type { EditorState, Transaction } from '@tiptap/pm/state';
import type { ComponentPublicInstance } from 'vue';
import type { InvitableUser } from '#layers/access/composables/invitations/use-invitations';
import type { UserInvitationAttributes } from '#layers/access/utils/extensions/user-invitation';
import type { MonospaceUser } from '#layers/data-model';
import { Text } from '@tiptap/extension-text';
import { Dropcursor, Gapcursor, UndoRedo } from '@tiptap/extensions';
import { EditorContent, useEditor } from '@tiptap/vue-3';
import { tv } from 'tailwind-variants';
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from '#imports';
import { useInvitations } from '#layers/access/composables/invitations/use-invitations';
import { isValidEmail } from '#layers/access/utils/email';
import { UserInvitation } from '#layers/access/utils/extensions/user-invitation';
import { isNil } from '#layers/base';

interface EmailRecipientsInputProps {
	showExistingUsers?: boolean;
	projectId?: string;
	excludeUserIds?: string[];
}

const props = withDefaults(defineProps<EmailRecipientsInputProps>(), {
	showExistingUsers: false,
	excludeUserIds: () => [],
});

const model = defineModel<UserInvitationAttributes[]>({ default: () => [] });
const pendingEmail = defineModel<string | null>('pendingEmail', { default: null });

const store = useStore();
const textareaTheme = useMergedTheme({ textarea: nuxtUiTextareaTheme });
const theme = computed(() => tv(textareaTheme)());

const { emitFormBlur, emitFormFocus, emitFormInput, emitFormChange, ariaAttrs } = useFormField<EmailRecipientsInputProps>(props as any, { deferInputValidation: true });

// Query org users when showExistingUsers is enabled. Service accounts are excluded:
// they aren't invitable and have no email (which the suggestion match relies on).
const { data: availableUsers } = store.MonospaceUser.query((q) => q.many(props.showExistingUsers
	? {
			filter: {
				isService: false,
				...(props.projectId
					? { projects: { _none: { projectId: props.projectId } } }
					: {}),
			},
			fetchPolicy: 'no-cache',
		}
	: { enabled: false }));

// Use composable for invitation management logic
const {
	getSuggestions,
	toEditorContent,
	fromEditorJson,
} = useInvitations({
	invitations: model,
	availableUsers,
	excludeUserIds: () => props.excludeUserIds,
});

const editor = useEditor({
	extensions: [
		InlineDocument,
		Text,
		UserInvitation.configure({
			onPasteEmails: (emails) => {
				clearTextContent();

				for (const email of emails) {
					editor.value?.commands.insertInvitation({
						email,
						recipientType: 'new_email',
					});
				}
			},
			isDuplicate: (email) => model.value.some((r) => r.email.toLowerCase() === email),
			getUser: (userId) => availableUsers.value?.find((u) => u.id === userId),
		}),
		InlineSuggestion.configure({
			onAccept: (item) => {
				const user = suggestions.value.find((u) => u.id === item.id);
				if (user) selectUser(user);
			},
		}),
		Dropcursor,
		Gapcursor,
		UndoRedo,
	],
	editorProps: {
		attributes: {
			class: theme.value.base({ class: 'w-full min-h-24 overflow-y-auto p-2 pt-1.5 leading-7 [&>*]:me-1' }),
		},
		handleKeyDown: (_view, event) => {
			// Navigation keys when suggestions are visible
			if (showSuggestions.value) {
				if (event.key === 'ArrowDown') {
					event.preventDefault();

					selectedSuggestionIndex.value = Math.min(
						selectedSuggestionIndex.value + 1,
						suggestions.value.length - 1,
					);

					return true;
				}

				if (event.key === 'ArrowUp') {
					event.preventDefault();

					selectedSuggestionIndex.value = Math.max(
						selectedSuggestionIndex.value - 1,
						0,
					);

					return true;
				}

				if (event.key === 'Enter') {
					event.preventDefault();
					const selected = suggestions.value[selectedSuggestionIndex.value];
					if (selected) selectUser(selected);
					return true;
				}

				if (event.key === 'Escape') {
					event.preventDefault();
					editor.value?.commands.blur();
					return true;
				}
			}

			// Email validation on Enter, comma, or space
			if (event.key === 'Enter' || event.key === ',' || event.key === ' ') {
				const textContent = getCurrentTextContent();

				if (textContent && isValidEmail(textContent)) {
					event.preventDefault();
					addEmailFromText(textContent);
					return true;
				}

				if (event.key === 'Enter' || event.key === ',') {
					event.preventDefault();
					return true;
				}
			}

			return false;
		},
	},
	content: toEditorContent(),
	onUpdate: ({ editor: editorInstance }) => {
		fromEditorJson(editorInstance.getJSON());
		updatePendingEmail();
		emitFormChange();
		emitFormInput();
	},
	onBlur: () => {
		const textContent = getCurrentTextContent();

		if (textContent && isValidEmail(textContent)) {
			addEmailFromText(textContent);
		}

		emitFormBlur();
	},
	onFocus: () => {
		emitFormFocus();
	},
});

onMounted(() => editor.value?.commands.focus());

function deleteAllTextNodes({ tr, state }: { tr: Transaction; state: EditorState }): boolean {
	const { doc } = state;
	const textNodes: { from: number; to: number }[] = [];

	doc.descendants((node, pos) => {
		if (node.type.name === 'text') {
			textNodes.push({ from: pos, to: pos + node.nodeSize });
		}
	});

	// Delete in reverse order to avoid position shifts
	for (let i = textNodes.length - 1; i >= 0; i--) {
		tr.delete(textNodes[i]!.from, textNodes[i]!.to);
	}

	return true;
}

function getCurrentTextContent(): string {
	if (!editor.value) return '';

	const { doc } = editor.value.state;
	let textContent = '';

	doc.nodesBetween(0, doc.content.size, (node) => {
		if (node.type.name === 'text') {
			textContent += node.text;
		}
	});

	return textContent.trim();
}

function updatePendingEmail() {
	const text = getCurrentTextContent();
	pendingEmail.value = (text && isValidEmail(text)) ? text.toLowerCase() : null;
}

function clearTextContent() {
	if (!editor.value) return;

	editor.value
		.chain()
		.focus()
		.command(deleteAllTextNodes)
		.run();
}

function addEmailFromText(email: string) {
	if (!editor.value) return;

	const normalizedEmail = email.trim().toLowerCase();

	// Check for duplicates
	if (model.value.some((r) => r.email.toLowerCase() === normalizedEmail)) {
		clearTextContent();
		return;
	}

	editor.value
		.chain()
		.focus()
		.command(deleteAllTextNodes)
		.insertInvitation({
			email: normalizedEmail,
			recipientType: 'new_email',
		})
		.run();

	const endPos = editor.value.state.doc.content.size;
	editor.value.chain().focus().setTextSelection(endPos).run();
}

function selectUser(user: InvitableUser) {
	if (!editor.value) return;
	if (isNil(user.email)) return;

	clearTextContent();

	editor.value
		.chain()
		.focus()
		.insertInvitation({
			email: user.email,
			recipientType: 'existing_member',
			userId: user.id,
			fullName: user.fullName,
		})
		.run();

	// Move cursor to end
	const endPos = editor.value.state.doc.content.size;
	editor.value.chain().setTextSelection(endPos).run();
}

// Use composable's getSuggestions with current search query
const searchQuery = computed(() => getCurrentTextContent());
const suggestions = computed(() => {
	if (!props.showExistingUsers) return [];
	return getSuggestions(searchQuery.value);
});

const showSuggestions = computed(() => suggestions.value.length > 0);

// Selection state for keyboard navigation
const selectedSuggestionIndex = ref(0);

// Reset selection when suggestions change and update inline suggestion
watch(suggestions, () => {
	selectedSuggestionIndex.value = 0;
});

// Wire suggestion selection to inline suggestion extension
watch([selectedSuggestionIndex, suggestions], () => {
	const selected = suggestions.value[selectedSuggestionIndex.value];

	if (selected) {
		editor.value?.commands.setSelectedSuggestion({
			id: selected.id,
			label: selected.fullName || selected.email || '',
			hintPrefix: selected.fullName?.split(' ')[0],
		});
	}
	else {
		editor.value?.commands.setSelectedSuggestion(null);
	}
});

function scrollSuggestionIntoView(el: Element | ComponentPublicInstance | null) {
	if (el instanceof Element) {
		el.scrollIntoView({ block: 'nearest' });
	}
}
</script>

<template>
	<div class="relative w-full">
		<div
			class="w-full"
			:class="[
				theme.root(),
			]"
		>
			<EditorContent
				:editor
				v-bind="ariaAttrs"
				class="w-full"
			/>
		</div>

		<div
			v-if="showSuggestions"
			class="absolute inset-x-0 top-full z-50 mt-1 rounded-md border border-default bg-default shadow-lg"
		>
			<div class="max-h-48 overflow-y-auto p-1">
				<button
					v-for="(user, index) in suggestions"
					:key="user.id"
					:ref="(el) => {
						if (index === selectedSuggestionIndex) scrollSuggestionIntoView(el)
					}"
					type="button"
					class="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left"
					:class="[
						index === selectedSuggestionIndex ? 'bg-accented' : 'hover:bg-muted',
					]"
					@click="selectUser(user)"
					@mouseenter="selectedSuggestionIndex = index"
				>
					<UserAvatar
						:user="user as MonospaceUser"
						class="size-6"
					/>
					<div class="flex flex-col">
						<span class="text-sm font-medium">{{ user.fullName || user.email }}</span>
						<span
							v-if="user.fullName"
							class="text-xs text-dimmed"
						>{{ user.email }}</span>
					</div>
				</button>
			</div>
		</div>
	</div>
</template>
